import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { interviewsV2, errorText, STATUS_STYLE, OUTCOME_LABEL, MODE_LABEL } from './interviewApi';

/** Status pill used by the page and the candidate card. */
export function StatusPill({ interview }) {
  const s = interview.status || 'scheduled';
  const label = s === 'completed' && interview.outcome ? OUTCOME_LABEL[interview.outcome] || 'Completed'
    : s === 'no_show' ? 'No-show' : s.charAt(0).toUpperCase() + s.slice(1);
  const late = s === 'scheduled' && interview.scheduled_at && new Date(interview.scheduled_at) < new Date();
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${late ? 'bg-rose-50 text-rose-700 border-rose-200' : STATUS_STYLE[s] || STATUS_STYLE.scheduled}`}>
      {late ? 'Awaiting outcome' : label}
    </span>
  );
}

export function ModePill({ mode }) {
  return <Badge variant="outline" className="text-[11px] font-normal">{MODE_LABEL[mode] || mode || '-'}</Badge>;
}

/** Outcome capture (complete / no-show). */
export function OutcomeDialog({ open, onOpenChange, interview, onDone, presetOutcome }) {
  const [outcome, setOutcome] = useState(presetOutcome || 'pending');
  const [notes, setNotes] = useState('');
  const [rating, setRating] = useState('');
  const [saving, setSaving] = useState(false);
  React.useEffect(() => { if (open) { setOutcome(presetOutcome || 'pending'); setNotes(''); setRating(''); } }, [open, presetOutcome]);
  if (!interview) return null;
  const submit = async () => {
    setSaving(true);
    try {
      const r = await interviewsV2.complete(interview.id, { outcome, notes: notes.trim() || null, rating: rating ? Number(rating) : null });
      toast.success(outcome === 'no_show' ? 'Marked as no-show - reminders cancelled' : `Outcome recorded: ${OUTCOME_LABEL[outcome]}`);
      onOpenChange(false);
      if (onDone) onDone(r);
    } catch (e) { toast.error(errorText(e.message)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{outcome === 'no_show' ? 'Mark no-show' : 'Record outcome'} - {interview.candidate_name}</DialogTitle>
          <DialogDescription>{interview.round_name} {interview.client_name ? `@ ${interview.client_name}` : ''}. This closes the recruiter task and stops any pending reminders.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Outcome</Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger data-testid="iv2-outcome"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(OUTCOME_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {outcome !== 'no_show' && (
            <div>
              <Label>Interviewer rating (1-5, optional)</Label>
              <Input type="number" min={1} max={5} value={rating} onChange={(e) => setRating(e.target.value)} />
            </div>
          )}
          <div>
            <Label>Feedback / notes</Label>
            <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={outcome === 'no_show' ? 'Did the candidate respond? Reschedule?' : 'What the client said, next steps, salary discussed...'} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Back</Button>
          <Button onClick={submit} disabled={saving} data-testid="iv2-outcome-submit">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Cancel with a reason - sends METHOD:CANCEL invites and cancels reminders. */
export function CancelDialog({ open, onOpenChange, interview, onDone }) {
  const [reason, setReason] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);
  React.useEffect(() => { if (open) { setReason(''); setSendEmail(true); } }, [open]);
  if (!interview) return null;
  const submit = async () => {
    setSaving(true);
    try {
      const r = await interviewsV2.cancel(interview.id, { reason: reason.trim() || null, send_email: sendEmail, send_whatsapp: false });
      toast.success(`Interview cancelled - ${r.reminders_cancelled || 0} reminder(s) stopped${sendEmail ? ', cancellation invites sent' : ''}`);
      onOpenChange(false);
      if (onDone) onDone(r);
    } catch (e) { toast.error(errorText(e.message)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel interview - {interview.candidate_name}</DialogTitle>
          <DialogDescription>Candidate and client contact get a cancellation email with a calendar update that removes the event. To move it instead, use Reschedule.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Reason</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Position closed / candidate withdrew / client asked to hold" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} /> Send cancellation emails (.ics CANCEL)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Keep it</Button>
          <Button variant="destructive" onClick={submit} disabled={saving} data-testid="iv2-cancel-submit">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Cancel interview</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
